import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useAuth } from "utils/useAuth/useAuth";
import { PortalContext } from "App";
import { searchErrorHandler } from "utils/formValidation";
import {
  checkMoneyDataForTable,
  checkNonMoneyDataForTable,
  trimObject,
} from "utils/utils";
import useThrottleCallback from "utils/useThrottleCallback/useThrottleCallback";
import Grid from "components/Grid/Grid";
import Datepicker from "components/Input/Datepicker/Datepicker";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import paymentGatewayOptions from "utils/paymentGatewayOptions/PaymentGatewayOptions.jsx";
import cardTypeOptions from "utils/cardTypeOptions/CardTypeOptions.jsx";
import transactionCurrencyOptions from "utils/transactionCurrencyOptions/TransactionCurrencyOptions.jsx";
import s from "./TransactionActivity.module.scss";
import { exportCsv, queryList, queryReferrer, querySales } from "./model";

const TransactionActivity = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const tabContext = useContext(PortalContext);
  const { currentPath } = tabContext;
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "transactionActivityGrid";
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [collapse, setCollapse] = useState(true);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searching, setSearching] = useState(false);
  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [salesList, setSalesList] = useState([]);
  const [referrerList, setReferrerList] = useState([]);
  const [searchFieldInput, setSearchFieldInput] = useState({
    startDate: null,
    endDate: null,
    createdStart: null,
    createdEnd: null,
    merchantId: "",
    merchantName: "",
    paymentGateway: "",
    cardType: "",
    currency: "",
    sales: "",
    referrer: "",
  });
  const [focus, setFocus] = useState({});
  const [error, setError] = useState({
    startDate: false,
    endDate: false,
    startDateLaterThanEndDate: false,
    createdStart: false,
    createdEnd: false,
    createdStartLaterThanEnd: false,
  });

  const [columnsArray, setColumnsArray] = useState([]);

  const windowResizeHandler = useThrottleCallback(() => {
    setWindowWidth(window.innerWidth);
  }, 20);
  useEffect(() => {
    if (currentPath === path) {
      const maxHeight = 76;
      const grid = document.getElementById(gridId);
      if (grid.scrollHeight - 15 <= maxHeight) {
        setCollapse(undefined);
      } else {
        if (collapse === undefined || collapse) {
          setCollapse(true);
        } else {
          setCollapse(false);
        }
      }
    }
    window.addEventListener("resize", windowResizeHandler);
    return () => {
      removeEventListener("resize", windowResizeHandler);
    };
  }, [windowWidth, currentPath]);

  useEffect(() => {
    const init = async () => {
      await getSales();
      await getReferrer();
      await initialFetch();
    };
    init();
  }, []);

  const initialFetch = async () => {
    setLoading(true);
    const response = await queryList({
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
    });
    const result = response?.data;
    if (result?.message === "Unauthorized") {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    } else if (result?.responseCode !== "0000") {
      snackbar(t("general.message.initial.fetch.error"), "error");
    } else if (result?.responseCode === "0000") {
      if (result.data.length === 0) {
        snackbar(t("general.message.no.result"), "warning");
      }
      setDatasource(result);
      setCurrentPage(result.page);
      setPageSize(result.size);
      pageSizeInfoRef.current.currentpage = result.page;
      pageSizeInfoRef.current.pagesize = result.size;
    }
    setSearch(false);
    setLoading(false);
  };

  const getSales = async () => {
    const response = await querySales({});
    const result = response?.data;
    if (result && result.data && Array.isArray(result.data)) {
      const newSalesList = [];
      result.data.forEach((sales) => {
        newSalesList.push({
          key: sales.id,
          title: sales.firstName,
          value: String(sales.id),
        });
      });
      setSalesList(newSalesList);
    } else {
      setSalesList([]);
    }
  };

  const getReferrer = async () => {
    const response = await queryReferrer({});
    const result = response?.data;
    if (result && result.data && Array.isArray(result.data)) {
      const newReferrerList = [];
      result.data.forEach((referrer) => {
        newReferrerList.push({
          key: referrer.id,
          title: referrer.company,
          value: String(referrer.id),
        });
      });
      setReferrerList(newReferrerList);
    } else {
      setReferrerList([]);
    }
  };

  const resetInputField = () => {
    setSearchFieldInput({
      startDate: null,
      endDate: null,
      createdStart: null,
      createdEnd: null,
      merchantId: "",
      merchantName: "",
      paymentGateway: "",
      cardType: "",
      currency: "",
      sales: "",
      referrer: "",
    });
  };

  const resetError = () => {
    setError({
      startDate: false,
      endDate: false,
      startDateLaterThanEndDate: false,
      createdStart: false,
      createdEnd: false,
      createdStartLaterThanEnd: false,
    });
  };

  const handleSearchFieldCollapse = (status) => {
    setCollapse(status);
  };

  const handleReset = async () => {
    resetInputField();
    resetError();
    await initialFetch();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      const { errorObj, hasError } = searchErrorHandler(searchFieldInput, [
        (input) =>
          ((input.startDate && !input.endDate) || input.endDate === "") && {
            endDate: true,
          },
        (input) =>
          ((input.endDate && !input.startDate) || input.startDate === "") && {
            startDate: true,
          },
        (input) =>
          moment(input.startDate).isAfter(input.endDate) && {
            startDateLaterThanEndDate: true,
          },
        (input) =>
          ((input.createdStart && !input.createdEnd) ||
            input.createdEnd === "") && { createdEnd: true },
        (input) =>
          ((input.createdEnd && !input.createdStart) ||
            input.createdStart === "") && { createdStart: true },
        (input) =>
          moment(input.createdStart).isAfter(input.createdEnd) && {
            createdStartLaterThanEnd: true,
          },
      ]);
      setError(errorObj);
      if (hasError) return;

      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    const params = trimObject({ ...searchFieldInput });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const response = await queryList(payload);
    const result = response?.data;
    if (result?.message === "Unauthorized") {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    } else if (result?.responseCode !== "0000") {
      snackbar(
        t("general.message.search.error") + ": " + result?.message,
        "error",
      );
    } else if (result?.responseCode === "0000") {
      if (result.data.length === 0) {
        snackbar(t("general.message.no.result"), "warning");
      }
      setDatasource(result);
      setCurrentPage(result.page);
      setPageSize(result.size);
      pageSizeInfoRef.current.currentpage = result.page;
      pageSizeInfoRef.current.pagesize = result.size;
    }
    setSearch(true);
    setSearching(false);
  };

  const handleExport = async () => {
    if (searchFieldInput.startDate && !searchFieldInput.endDate) {
      snackbar(t("general.message.export.function.error.message.2"), "warning");
      return;
    }
    if (searchFieldInput.endDate && !searchFieldInput.startDate) {
      snackbar(t("general.message.export.function.error.message.1"), "warning");
      return;
    }
    if (searchFieldInput.startDate > searchFieldInput.endDate) {
      snackbar(t("general.message.export.function.error.message.3"), "warning");
      return;
    }
    if (
      moment(searchFieldInput.endDate)
        .endOf("day")
        .diff(moment(searchFieldInput.startDate).startOf("day"), "months") > 5
    ) {
      snackbar(t("general.message.export.function.error.message.4"), "warning");
      return;
    }

    if (searchFieldInput.createdStart && !searchFieldInput.createdEnd) {
      snackbar(t("general.message.export.function.error.message.2"), "warning");
      return;
    }
    if (searchFieldInput.createdEnd && !searchFieldInput.createdStart) {
      snackbar(t("general.message.export.function.error.message.1"), "warning");
      return;
    }
    if (searchFieldInput.createdStart > searchFieldInput.createdEnd) {
      snackbar(t("general.message.export.function.error.message.3"), "warning");
      return;
    }
    if (
      moment(searchFieldInput.createdEnd)
        .endOf("day")
        .diff(moment(searchFieldInput.createdStart).startOf("day"), "months") >
      5
    ) {
      snackbar(t("general.message.export.function.error.message.4"), "warning");
      return;
    }
    setExporting(true);
    const payload = trimObject({ ...searchFieldInput });
    const response = await exportCsv(payload);
    const result = response?.data;
    setExporting(false);
    if (result && result.responseCode === "0000") {
      snackbar(t("general.message.export.function.success.message"), "success");
      const url = result.data;
      window.location = url;
    }
    if (result && result.responseCode === "2200") {
      return snackbar(
        t("general.message.export.function.error.message.5"),
        "warning",
      );
    }
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const textInputOnchange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };
  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };
  const textInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  const transactionDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    const date = moment(rawDate).isValid()
      ? moment(rawDate).format("YYYY-MM-DD")
      : e !== null
        ? ""
        : null;
    if (type === "start") {
      setError({
        ...error,
        startDate: false,
        startDateLaterThanEndDate: false,
      });
      setSearchFieldInput({ ...searchFieldInput, startDate: date });
    } else if (type === "end") {
      setError({ ...error, endDate: false, startDateLaterThanEndDate: false });
      setSearchFieldInput({ ...searchFieldInput, endDate: date });
    }
  };
  const dateFormatValidator = (dateValue) => {
    const isValidDateFormat = moment(dateValue).isValid();
    return isValidDateFormat;
  };

  const onboardDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    const date = moment(rawDate).isValid()
      ? moment(rawDate).format("YYYY-MM-DD")
      : e !== null
        ? ""
        : null;
    if (type === "start") {
      setError({
        ...error,
        createdStart: false,
        createdStartLaterThanEnd: false,
      });
      setSearchFieldInput({ ...searchFieldInput, createdStart: date });
    } else if (type === "end") {
      setError({
        ...error,
        createdEnd: false,
        createdStartLaterThanEnd: false,
      });
      setSearchFieldInput({ ...searchFieldInput, createdEnd: date });
    }
  };

  const columns = [
    {
      index: 0,
      key: "merchantId",
      title: t("page.analytics.transaction.activity.column.title.merchant.id"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 1,
      key: "merchantName",
      title: t(
        "page.analytics.transaction.activity.column.title.merchant.name",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 2,
      key: "paymentGateway",
      title: t(
        "page.analytics.transaction.activity.column.title.payment.gateway",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 3,
      key: "cardType",
      title: t("page.analytics.transaction.activity.column.title.card.type"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 4,
      key: "currency",
      title: t(
        "page.analytics.transaction.activity.column.title.transaction.currency",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 5,
      key: "totalAmount",
      title: t("page.analytics.transaction.activity.column.title.total.amount"),
      type: "amount",
      sorter: false,
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
    {
      index: 6,
      key: "salesCommission",
      title: t(
        "page.analytics.transaction.activity.column.title.sales.commission",
      ),
      type: "amount",
      sorter: false,
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
    {
      index: 7,
      key: "referrerCommission",
      title: t(
        "page.analytics.transaction.activity.column.title.referrer.commission",
      ),
      type: "amount",
      sorter: false,
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
    {
      index: 8,
      key: "sales",
      title: t("page.analytics.transaction.activity.column.title.sales"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 9,
      key: "referrer",
      title: t("page.analytics.transaction.activity.column.title.referrer"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 10,
      key: "action",
      title: t("page.analytics.transaction.activity.column.title.action"),
      sorter: false,
      render: (data) => {
        const { merchantId } = data;
        const payload = {
          name: "Merchant Detail",
          path: "/analytics/transaction-activity/merchant-detail",
          state: {
            merchantId: merchantId,
          },
        };

        return (
          <div className={s.actionBtnContainer}>
            <button onClick={() => redirectFn("redirectPage", payload)}>
              {t("page.analytics.transaction.activity.column.title.action.1")}
            </button>
          </div>
        );
      },
    },
  ];

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 50, 100],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId} collapse={collapse}>
          <Datepicker
            label={t(
              "page.analytics.transaction.activity.transaction.start.date.label",
            )}
            errorMessage={
              error.startDateLaterThanEndDate
                ? t(
                    "page.analytics.transaction.activity.transaction.start.date.error.message.2",
                  )
                : t(
                    "page.analytics.transaction.activity.transaction.start.date.error.message",
                  )
            }
            type="start"
            value={searchFieldInput.startDate}
            limitValue={searchFieldInput.endDate}
            onchangeFn={transactionDateOnChange}
            onfocusFn={() => textInputOnFocus("startDate")}
            onblurFn={() => textInputOnBlur("startDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.startDate,
              datepickerError:
                error.startDate || error.startDateLaterThanEndDate,
            }}
          />
          <Datepicker
            label={t(
              "page.analytics.transaction.activity.transaction.end.date.label",
            )}
            errorMessage={
              error.startDateLaterThanEndDate
                ? t(
                    "page.analytics.transaction.activity.transaction.end.date.error.message.2",
                  )
                : t(
                    "page.analytics.transaction.activity.transaction.end.date.error.message",
                  )
            }
            type="end"
            value={searchFieldInput.endDate}
            limitValue={searchFieldInput.startDate}
            onchangeFn={transactionDateOnChange}
            onfocusFn={() => textInputOnFocus("endDate")}
            onblurFn={() => textInputOnBlur("endDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.endDate,
              datepickerError: error.endDate || error.startDateLaterThanEndDate,
            }}
          />
          <Datepicker
            label={t(
              "page.analytics.transaction.activity.onboard.start.date.label",
            )}
            errorMessage={
              error.createdStartLaterThanEnd
                ? t(
                    "page.analytics.transaction.activity.onboard.start.date.error.message.2",
                  )
                : t(
                    "page.analytics.transaction.activity.onboard.start.date.error.message",
                  )
            }
            type="start"
            value={searchFieldInput.createdStart}
            limitValue={searchFieldInput.createdEnd}
            onchangeFn={onboardDateOnChange}
            onfocusFn={() => textInputOnFocus("createdStart")}
            onblurFn={() =>
              textInputOnBlur("createdStart", dateFormatValidator)
            }
            sxParams={{
              datepickerFocus: focus.createdStart,
              datepickerError:
                error.createdStart || error.createdStartLaterThanEnd,
            }}
          />
          <Datepicker
            label={t(
              "page.analytics.transaction.activity.onboard.end.date.label",
            )}
            errorMessage={
              error.createdStartLaterThanEnd
                ? t(
                    "page.analytics.transaction.activity.onboard.end.date.error.message.2",
                  )
                : t(
                    "page.analytics.transaction.activity.onboard.end.date.error.message",
                  )
            }
            type="end"
            value={searchFieldInput.createdEnd}
            limitValue={searchFieldInput.createdStart}
            onchangeFn={onboardDateOnChange}
            onfocusFn={() => textInputOnFocus("createdEnd")}
            onblurFn={() => textInputOnBlur("createdEnd", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.createdEnd,
              datepickerError:
                error.createdEnd || error.createdStartLaterThanEnd,
            }}
          />
          <OutlinedText
            label={t("page.analytics.transaction.activity.merchant.id.label")}
            value={searchFieldInput.merchantId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "merchantId")}
            onfocusFn={() => textInputOnFocus("merchantId")}
            onblurFn={() => textInputOnBlur("merchantId")}
            sxParams={{ textFocus: focus.merchantId }}
          />
          <OutlinedText
            label={t("page.analytics.transaction.activity.merchant.name.label")}
            value={searchFieldInput.merchantName}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "merchantName")}
            onfocusFn={() => textInputOnFocus("merchantName")}
            onblurFn={() => textInputOnBlur("merchantName")}
            sxParams={{ textFocus: focus.merchantName }}
          />
          <Selection
            label={t(
              "page.analytics.transaction.activity.payment.gateway.label",
            )}
            selectionValue={searchFieldInput.paymentGateway}
            onchangeFn={(e) => textInputOnchange(e, "paymentGateway")}
            onfocusFn={() => textInputOnFocus("paymentGateway")}
            onblurFn={() => textInputOnBlur("paymentGateway")}
            options={paymentGatewayOptions("all")}
            sxParams={{ selectionFocus: focus.paymentGateway }}
          />
          <Selection
            label={t("page.analytics.transaction.activity.card.type.label")}
            selectionValue={searchFieldInput.cardType}
            onchangeFn={(e) => textInputOnchange(e, "cardType")}
            onfocusFn={() => textInputOnFocus("cardType")}
            onblurFn={() => textInputOnBlur("cardType")}
            options={cardTypeOptions("all")}
            sxParams={{ selectionFocus: focus.cardType }}
          />
          <Selection
            label={t(
              "page.analytics.transaction.activity.transaction.currency.label",
            )}
            selectionValue={searchFieldInput.currency}
            onchangeFn={(e) => textInputOnchange(e, "currency")}
            onfocusFn={() => textInputOnFocus("currency")}
            onblurFn={() => textInputOnBlur("currency")}
            options={transactionCurrencyOptions("all")}
            sxParams={{ selectionFocus: focus.currency }}
          />
          <Selection
            label={t("page.analytics.transaction.activity.sales.label")}
            selectionValue={searchFieldInput.sales}
            onchangeFn={(e) => textInputOnchange(e, "sales")}
            onfocusFn={() => textInputOnFocus("sales")}
            onblurFn={() => textInputOnBlur("sales")}
            options={salesList}
            sxParams={{ selectionFocus: focus.sales }}
          />
          <Selection
            label={t("page.analytics.transaction.activity.referrer.label")}
            selectionValue={searchFieldInput.referrer}
            onchangeFn={(e) => textInputOnchange(e, "referrer")}
            onfocusFn={() => textInputOnFocus("referrer")}
            onblurFn={() => textInputOnBlur("referrer")}
            options={referrerList}
            sxParams={{ selectionFocus: focus.referrer }}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching || exporting}
          collapse={collapse}
          handleCollapse={handleSearchFieldCollapse}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <div className={s.descriptionBarContainer}>
          <div className={s.descriptionBar}>
            <span>{t("component.remarks.label")}</span>
            <span>{t("page.analytics.transaction.activity.description")}</span>
          </div>
          <FunctionalButtonBar
            loading={loading || searching || exporting}
            exporting={exporting}
            exportBtn
            handleExport={handleExport}
            refreshBtn
            handleRefresh={handleSearch}
            columnDisplayBtn
            columnDisplayProps={{
              columns,
              columnsArray,
              setColumnsArray,
              rememberPreference: "analytics-transaction-activity",
            }}
          />
        </div>
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}

          {searching && <Spin type={"search"} />}
        </div>
      </div>
    </div>
  );
};

export default React.memo(TransactionActivity);
